import { useEffect, useRef } from 'react';

const EDGE_FN_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/chat-lead-capture`;

// Patterns to extract contact info from chat messages
const NAME_PATTERNS = [
  /(?:my name is|i(?:'m| am)|call me)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
  /^([A-Z][a-z]+\s+[A-Z][a-z]+)$/m,
];
const EMAIL_PATTERN = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;
const PHONE_PATTERN = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/;
const COVERAGE_PATTERNS = [
  /\$[\d,]+(?:k|K|,000)?(?:\s*(?:thousand|million))?/,
  /(\d+)k\s*(?:in\s*)?coverage/i,
];
const GOALS_PATTERNS = [
  /(?:interested in|looking for|need|want)\s+(term|whole|burial|final expense|senior|no.?exam|annuit\w+|life insurance\w*)/i,
];
const AGE_PATTERN = /(?:i(?:'m| am)\s+)?(\d{2})\s*(?:years?\s*old)?/i;
const ZIP_PATTERN = /\b(\d{5})\b/;

interface ExtractedLead {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  goals?: string;
  coverage_amount?: string;
  age_range?: string;
  zip_code?: string;
  notes?: string;
}

function extractLeadData(messages: string[]): ExtractedLead {
  const fullText = messages.join(' ');
  const lead: ExtractedLead = {};

  // Name
  for (const pattern of NAME_PATTERNS) {
    const match = fullText.match(pattern);
    if (match) {
      const parts = match[1].trim().split(/\s+/);
      lead.first_name = parts[0];
      if (parts.length > 1) lead.last_name = parts.slice(1).join(' ');
      break;
    }
  }

  // Email
  const emailMatch = fullText.match(EMAIL_PATTERN);
  if (emailMatch) lead.email = emailMatch[0].toLowerCase();

  // Phone
  const phoneMatch = fullText.match(PHONE_PATTERN);
  if (phoneMatch) lead.phone = phoneMatch[0].replace(/\D/g, '').replace(/^1/, '');

  // Goals / insurance type
  for (const pattern of GOALS_PATTERNS) {
    const match = fullText.match(pattern);
    if (match) {
      lead.goals = match[1];
      break;
    }
  }

  // Coverage amount
  for (const pattern of COVERAGE_PATTERNS) {
    const match = fullText.match(pattern);
    if (match) {
      lead.coverage_amount = match[0];
      break;
    }
  }

  // Age
  const ageMatch = fullText.match(AGE_PATTERN);
  if (ageMatch) {
    const age = parseInt(ageMatch[1], 10);
    if (age >= 18 && age <= 90) lead.age_range = String(age);
  }

  // ZIP code
  const zipMatch = fullText.match(ZIP_PATTERN);
  if (zipMatch) lead.zip_code = zipMatch[1];

  // Build notes summary from all messages
  if (messages.length > 0) {
    lead.notes = messages.slice(0, 8).join(' | ').substring(0, 500);
  }

  return lead;
}

function hasMinimumData(lead: ExtractedLead): boolean {
  const hasName = !!(lead.first_name || lead.last_name);
  const hasContact = !!(lead.email || lead.phone);
  return hasName && hasContact;
}

export function useChatLeadBridge() {
  const capturedRef = useRef(false);
  const userMessagesRef = useRef<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only process messages from the Readdy widget iframe
      if (!event.data || typeof event.data !== 'object') return;

      const { type, payload } = event.data as { type?: string; payload?: Record<string, unknown> };

      // Readdy widget emits these event types
      if (
        type === 'readdy:message' ||
        type === 'readdy:user_message' ||
        type === 'readdy:chat_message'
      ) {
        const text = (payload?.text || payload?.message || payload?.content || '') as string;
        if (text && typeof text === 'string' && text.trim().length > 2) {
          userMessagesRef.current.push(text.trim());

          // Debounce: wait 3s after last message before attempting extraction
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            if (capturedRef.current) return;

            const lead = extractLeadData(userMessagesRef.current);
            if (hasMinimumData(lead)) {
              capturedRef.current = true;
              submitChatLead(lead);
            }
          }, 3000);
        }
      }

      // Also handle explicit lead capture event if widget emits one
      if (type === 'readdy:lead_captured' && payload) {
        if (capturedRef.current) return;
        capturedRef.current = true;

        const lead: ExtractedLead = {
          first_name: payload.first_name as string | undefined,
          last_name: payload.last_name as string | undefined,
          email: payload.email as string | undefined,
          phone: payload.phone as string | undefined,
          goals: payload.goals as string | undefined,
          notes: payload.notes as string | undefined,
        };

        if (hasMinimumData(lead)) {
          submitChatLead(lead);
        }
      }
    };

    // Also poll the DOM for any Readdy-injected data attributes
    const domPollInterval = setInterval(() => {
      if (capturedRef.current) {
        clearInterval(domPollInterval);
        return;
      }

      // Check if widget has exposed lead data on window
      const w = window as unknown as Record<string, unknown>;
      const readdyLead = w.__readdy_lead__ || w.readdyLeadData;
      if (readdyLead && typeof readdyLead === 'object') {
        const lead = readdyLead as ExtractedLead;
        if (hasMinimumData(lead)) {
          capturedRef.current = true;
          clearInterval(domPollInterval);
          submitChatLead(lead);
        }
      }
    }, 5000);

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(domPollInterval);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
}

async function submitChatLead(lead: ExtractedLead) {
  try {
    // Format phone to standard format if present
    let phone = lead.phone;
    if (phone && phone.length === 10) {
      phone = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
    }

    const payload = {
      ...lead,
      phone,
      page_url: window.location.pathname,
    };

    const res = await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      const data = await res.json();
      if (!data.duplicate) {
        console.info('[ChatBridge] Chat lead captured successfully:', data.lead_id);
      }
    } else {
      console.warn('[ChatBridge] Failed to submit chat lead:', res.status);
    }
  } catch (err) {
    // Silent fail — never interrupt the user experience
    console.warn('[ChatBridge] Error submitting chat lead:', err);
  }
}
